/*
 * Harmony Arts: progressive infinite scroll for collection templates.
 * Native page links stay in the document for SEO and as a failure fallback.
 * No apps or catalog/collection changes required.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window) || !('fetch' in window)) return;

  var current = null;
  var pendingRefresh = false;
  var setupStarted = false;

  function normalizeNext(href) {
    if (!href) return '';
    var url;
    try { url = new URL(href, window.location.href); } catch (_) { return ''; }
    if (url.origin !== window.location.origin) return '';
    if (url.pathname !== window.location.pathname) return '';
    var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', preview);
    }
    return url.href;
  }

  function release(state) {
    if (!state) return;
    state.disposed = true;
    if (state.observer) state.observer.disconnect();
    if (state.controller) state.controller.abort();
  }

  function makeStatus() {
    var status = document.createElement('div');
    status.className = 'harmony-infinite-scroll-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'text-align:center;padding:22px 12px;min-height:55px;clear:both;';
    status.textContent = 'Scroll to see more paintings';
    return status;
  }

  function refresh() {
    pendingRefresh = false;
    var section = document.querySelector('#CollectionSection');
    var grid = section && section.querySelector('.grid--collection');
    var pagination = section && section.querySelector('.pagination[data-harmony-next-url]');
    if (current && current.section === section && current.pagination === pagination) return;
    release(current);
    current = null;
    if (!section || !grid || !pagination) return;

    var next = normalizeNext(pagination.getAttribute('data-harmony-next-url'));
    if (!next) return;

    var status = makeStatus();
    pagination.insertAdjacentElement('afterend', status);
    var state = {
      section: section,
      grid: grid,
      pagination: pagination,
      status: status,
      next: next,
      loading: false,
      disposed: false,
      controller: null,
      observer: null,
      previousDisplay: pagination.style.display,
      visited: new Set()
    };
    current = state;

    // Only hide numbered links when automated loading has been initialized.
    pagination.style.display = 'none';
    state.observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) {
        loadNext(state);
      }
    }, { rootMargin: '800px 0px', threshold: 0 });
    state.observer.observe(status);
  }

  function fail(state) {
    if (current !== state || state.disposed) return;
    state.next = '';
    if (state.observer) state.observer.disconnect();
    state.pagination.style.display = state.previousDisplay;
    state.status.textContent = 'Automatic loading was interrupted. Use the page links below to continue.';
  }

  function finish(state) {
    if (state.observer) state.observer.disconnect();
    state.status.textContent = 'All paintings in this collection have been loaded.';
  }

  async function loadNext(state) {
    if (state.loading || state.disposed || !state.next || current !== state) return;
    if (!state.section.isConnected || document.querySelector('#CollectionSection') !== state.section) return;

    var url = state.next;
    if (state.visited.has(url)) return fail(state);
    state.visited.add(url);
    state.loading = true;
    state.status.textContent = 'Loading more paintings…';
    state.controller = new AbortController();

    try {
      var response = await fetch(url, {
        credentials: 'same-origin',
        signal: state.controller.signal,
        headers: { 'Accept': 'text/html' }
      });
      if (!response.ok) throw new Error('Collection page request failed');
      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var pageSection = doc.querySelector('#CollectionSection');
      var pageGrid = pageSection && pageSection.querySelector('.grid--collection');
      if (!pageGrid || !pageGrid.querySelector('.grid-product')) {
        throw new Error('No product grid in next page');
      }

      // Filtering and sorting replace CollectionSection by AJAX. Ignore stale
      // responses from a prior selection and abort in-flight old requests.
      if (current !== state || state.disposed || !state.section.isConnected ||
          document.querySelector('#CollectionSection') !== state.section) return;

      var already = new Set(
        Array.from(state.grid.querySelectorAll('.grid-product[data-product-handle]'))
          .map(function (element) { return element.getAttribute('data-product-handle'); })
      );
      var fragment = document.createDocumentFragment();
      var added = 0;
      Array.from(pageGrid.children).forEach(function (element) {
        if (!element.matches('.grid-product')) return;
        var handle = element.getAttribute('data-product-handle');
        if (handle && already.has(handle)) return;
        if (handle) already.add(handle);
        var node = document.importNode(element, true);
        node.querySelectorAll('img:not([loading])').forEach(function (img) {
          img.setAttribute('loading', 'lazy');
        });
        fragment.appendChild(node);
        added += 1;
      });
      if (!added) throw new Error('No new paintings found');
      state.grid.appendChild(fragment);

      // Keep quick-shop buttons and associated modals working on new cards.
      pageSection.querySelectorAll('.modal--quick-shop[id]').forEach(function (modal) {
        if (!state.section.querySelector('[id="' + modal.id + '"]')) {
          state.section.appendChild(document.importNode(modal, true));
        }
      });
      if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
        window.theme.reinitProductGridItem(window.jQuery ? window.jQuery(state.section) : undefined);
      }
      if (window.lazySizes && window.lazySizes.loader) {
        window.lazySizes.loader.checkElems();
      }
      document.dispatchEvent(new CustomEvent('theme:product:changed'));

      var newPagination = pageSection.querySelector('.pagination[data-harmony-next-url]');
      state.next = normalizeNext(newPagination && newPagination.getAttribute('data-harmony-next-url'));
      if (state.next && state.visited.has(state.next)) throw new Error('Repeated next page');
      if (state.next) {
        state.status.textContent = 'Scroll to see more paintings';
        // Reobserve to account for short collections where the sentinel
        // may remain visible after another batch has been appended.
        state.observer.unobserve(state.status);
        state.observer.observe(state.status);
      } else {
        finish(state);
      }
    } catch (error) {
      if (error.name !== 'AbortError') fail(state);
    } finally {
      state.loading = false;
      state.controller = null;
    }
  }

  function scheduleRefresh() {
    if (pendingRefresh) return;
    pendingRefresh = true;
    window.requestAnimationFrame(refresh);
  }

  function start() {
    if (setupStarted) return;
    setupStarted = true;
    refresh();
    // The Impulse theme replaces the entire collection section when a buyer
    // changes a filter, sort option, or history state.
    var changes = new MutationObserver(scheduleRefresh);
    changes.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();